import React, { useEffect, useState } from 'react';
import {
    FaTimes, FaDownload, FaFilePdf, FaFileWord, FaFileExcel,
    FaFilePowerpoint, FaFileImage, FaFileAlt, FaFileArchive,
    FaFileCode, FaFileAudio, FaExternalLinkAlt, FaSpinner,
    FaPlayCircle, FaExclamationTriangle, FaBookOpen
} from 'react-icons/fa';
import './FilePreviewModal.css';
import { resourcesAPI } from '../services/api';

// ── Detectar tipo de previsualización ────────────────────────────────────────
const detectPreviewType = (resource) => {
    if (!resource) return 'generic';

    const typeName = (
        resource.originalResource?.type?.name ||
        resource.type ||
        ''
    ).toLowerCase();

    const fileName = (
        resource.originalResource?.fileName ||
        resource.fileName ||
        ''
    ).toLowerCase();

    const humanName = (
        resource.originalResource?.name ||
        resource.name ||
        resource.title ||
        ''
    ).toLowerCase();

    // Determinar extensión buscando en fileName y luego en humanName
    const getExt = (str) => {
        if (!str) return '';
        const parts = str.split('.');
        return parts.length > 1 ? parts.pop().toLowerCase() : '';
    };

    let ext = getExt(fileName);
    if (!ext) ext = getExt(humanName);

    // Mapeo por extensión
    if (['pdf'].includes(ext)) return 'pdf';
    if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'].includes(ext)) return 'image';
    if (['mp4', 'webm', 'ogv', 'avi', 'mov', 'mkv'].includes(ext)) return 'video';
    if (['mp3', 'wav', 'ogg', 'aac', 'flac'].includes(ext)) return 'audio';
    if (['txt', 'csv', 'json', 'xml', 'md', 'log'].includes(ext)) return 'text';
    if (['doc', 'docx'].includes(ext)) return 'word';
    if (['xls', 'xlsx', 'ods'].includes(ext)) return 'excel';
    if (['ppt', 'pptx', 'odp'].includes(ext)) return 'powerpoint';
    if (['zip', 'rar', '7z', 'tar', 'gz'].includes(ext)) return 'archive';
    if (['js', 'ts', 'py', 'java', 'c', 'cpp', 'html', 'css', 'php'].includes(ext)) return 'code';

    // Mapeo por metadata de la DB (más estricto para evitar falsos positivos con "Documento")
    if (typeName.includes('pdf')) return 'pdf';
    if (typeName.includes('video')) return 'video';
    if (typeName.includes('imagen') || typeName.includes('image') || typeName.includes('foto')) return 'image';
    if (typeName.includes('audio') || typeName.includes('podcast')) return 'audio';
    if (typeName.includes('word') || typeName === 'doc' || typeName === 'docx' || typeName === '.doc' || typeName === '.docx') return 'word';
    if (typeName.includes('excel') || typeName.includes('hoja')) return 'excel';
    if (typeName.includes('plantilla') || typeName.includes('template') || typeName.includes('formato')) return 'word';
    if (typeName.includes('guía') || typeName.includes('guia') || typeName.includes('artículo')) return 'pdf';
    if (typeName.includes('presentación') || typeName.includes('presentacion')) return 'powerpoint';

    return 'generic';
};

// ── Metadatos visuales por tipo ──────────────────────────────────────────────
const TYPE_META = {
    pdf: { icon: FaFilePdf, color: '#e53e3e', label: 'PDF', previewable: true },
    image: { icon: FaFileImage, color: '#38a169', label: 'Imagen', previewable: true },
    audio: { icon: FaFileAudio, color: '#805ad5', label: 'Audio', previewable: true },
    text: { icon: FaFileAlt, color: '#3182ce', label: 'Texto', previewable: true },
    code: { icon: FaFileCode, color: '#6b46c1', label: 'Código', previewable: true },
    word: { icon: FaFileWord, color: '#2b6cb0', label: 'Word', previewable: false },
    excel: { icon: FaFileExcel, color: '#276749', label: 'Excel', previewable: false },
    powerpoint: { icon: FaFilePowerpoint, color: '#c05621', label: 'PowerPoint', previewable: false },
    archive: { icon: FaFileArchive, color: '#744210', label: 'Archivo', previewable: false },
    video: { icon: FaPlayCircle, color: '#FF6600', label: 'Video', previewable: true },
    generic: { icon: FaFileAlt, color: '#718096', label: 'Documento', previewable: false },
};

// ── Componente principal ─────────────────────────────────────────────────────
const FilePreviewModal = ({ resource, isOpen, onClose }) => {
    const [blobUrl, setBlobUrl] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [hasViewed, setHasViewed] = useState(false);

    const [overridePreviewType, setOverridePreviewType] = useState(null);

    const initialPreviewType = detectPreviewType(resource);
    const previewType = overridePreviewType || initialPreviewType;
    const meta = TYPE_META[previewType] || TYPE_META.generic;
    const IconComp = meta.icon;

    // ── Cargar archivo como Blob ──
    const loadFileBlob = async () => {
        if (!resource?.id) return;

        try {
            setLoading(true);
            setError(null);
            console.log(`📂 [FPM] Fetching resource ${resource.id} as blob (preview=true)...`);

            const response = await resourcesAPI.download(resource.id, { preview: 'true' });
            const blob = response.data;
            const url = URL.createObjectURL(blob);

            // Refinar el tipo según el MIME real devuelto por el servidor (basado en firmas mágicas)
            const mime = blob.type.toLowerCase();
            console.log('📂 [FPM] Detected MIME from server:', mime);

            if (mime === 'application/pdf') setOverridePreviewType('pdf');
            else if (mime.startsWith('image/')) setOverridePreviewType('image');
            else if (mime.startsWith('video/')) setOverridePreviewType('video');
            else if (mime.startsWith('audio/')) setOverridePreviewType('audio');
            else if (mime === 'text/plain' || mime === 'text/csv') setOverridePreviewType('text');
            else if (mime.includes('word') || mime.includes('officedocument.wordprocessingml')) setOverridePreviewType('word');
            else if (mime.includes('excel') || mime.includes('officedocument.spreadsheetml')) setOverridePreviewType('excel');
            else if (mime.includes('powerpoint') || mime.includes('officedocument.presentationml')) setOverridePreviewType('powerpoint');

            setBlobUrl(url);
            setLoading(false);
            setHasViewed(true);
        } catch (err) {
            console.error('❌ [FPM] Error loading file blob:', err);
            setError('No se pudo cargar la vista previa del archivo.');
            setLoading(false);
        }
    };

    useEffect(() => {
        const onKey = (e) => { if (e.key === 'Escape' && isOpen) onClose(); };

        if (isOpen) {
            document.addEventListener('keydown', onKey);
            document.body.style.overflow = 'hidden';
            setHasViewed(false);
            setOverridePreviewType(null); // Resetar tipo detectado anteriormente
            loadFileBlob();
        }

        return () => {
            document.removeEventListener('keydown', onKey);
            document.body.style.overflow = 'auto';

            // Cleanup Blob URL
            if (blobUrl) {
                console.log('🧹 [FPM] Revoking object URL');
                URL.revokeObjectURL(blobUrl);
                setBlobUrl(null);
            }
        };
    }, [isOpen, resource?.id]);

    if (!isOpen || !resource) return null;

    const handleDownload = () => {
        // Usar el blobUrl si ya lo tenemos, o el endpoint directamente
        const finalUrl = blobUrl || `${process.env.REACT_APP_API_BASE_URL || 'http://localhost:5000/api'}/resources/${resource.id}/download`;
        const a = document.createElement('a');
        a.href = finalUrl;
        a.download = resource.title || resource.name || 'archivo';
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
    };

    // ── Renderizar área de previsualización ──────────────────────────────────
    const renderPreview = () => {
        if (loading) {
            return (
                <div className="fpm-iframe-loading">
                    <FaSpinner className="fpm-spinner" />
                    <span>Preparando visualización...</span>
                </div>
            );
        }

        if (error) {
            return (
                <div className="fpm-error-msg">
                    <FaExclamationTriangle />
                    <p>{error}</p>
                    <button className="fpm-download-big" onClick={handleDownload}>
                        <FaDownload /> Intentar descargar directamente
                    </button>
                </div>
            );
        }

        switch (previewType) {
            case 'pdf':
                return (
                    <div className="fpm-preview-wrap">
                        <embed
                            src={`${blobUrl}#toolbar=1&view=FitH`}
                            type="application/pdf"
                            className="fpm-iframe"
                            onLoad={() => setHasViewed(true)}
                        />
                    </div>
                );

            case 'image':
                return (
                    <div className="fpm-preview-wrap fpm-preview-image">
                        <img
                            src={blobUrl}
                            alt={resource.title}
                            className="fpm-img"
                            onLoad={() => setHasViewed(true)}
                        />
                    </div>
                );

            case 'audio':
                return (
                    <div className="fpm-preview-wrap fpm-preview-audio">
                        <IconComp className="fpm-generic-icon" style={{ color: meta.color }} />
                        <p className="fpm-generic-name">{resource.title}</p>
                        <audio
                            controls
                            src={blobUrl}
                            className="fpm-audio"
                            onPlay={() => setHasViewed(true)}
                        >
                            Tu navegador no soporta audio.
                        </audio>
                    </div>
                );

            case 'video':
                return (
                    <div className="fpm-preview-wrap fpm-preview-video">
                        <video
                            controls
                            src={blobUrl}
                            className="fpm-video"
                            onPlay={() => setHasViewed(true)}
                        >
                            Tu navegador no soporta video.
                        </video>
                    </div>
                );

            case 'text':
            case 'code':
                return (
                    <div className="fpm-preview-wrap">
                        <iframe
                            src={blobUrl}
                            title={resource.title}
                            className="fpm-iframe"
                            onLoad={() => setHasViewed(true)}
                        />
                    </div>
                );

            default:
                // Word, Excel, PPT, genérico
                return (
                    <div className="fpm-preview-wrap fpm-preview-generic">
                        <IconComp className="fpm-generic-icon" style={{ color: meta.color }} />
                        <p className="fpm-generic-name">{resource.title || resource.name}</p>
                        <p className="fpm-generic-type">{meta.label}</p>
                        <p className="fpm-generic-msg">
                            Este tipo de archivo requiere una aplicación externa para su visualización.<br />
                            <strong>Descárgalo para abrirlo en tu equipo.</strong>
                        </p>
                        <button
                            className="fpm-download-big"
                            onClick={handleDownload}
                            disabled={!hasViewed}
                        >
                            <FaDownload /> {hasViewed ? 'Descargar ahora' : 'Preparando enlace...'}
                        </button>
                    </div>
                );
        }
    };

    return (
        <div className="fpm-overlay" onClick={onClose}>
            <div className="fpm-modal fpm-modal--immersive" onClick={(e) => e.stopPropagation()}>

                {/* Botón Cerrar Flotante 🔥 */}
                <button className="fpm-close-floating" onClick={onClose} title="Cerrar">
                    <FaTimes />
                </button>

                {/* ── Preview area (Ocupa todo el espacio) ── */}
                <div className="fpm-body">
                    {loading && (
                        <div className="fpm-iframe-loading">
                            <div className="fpm-spinner"><FaBookOpen /></div>
                            <p>Cargando vista previa profesional...</p>
                        </div>
                    )}

                    {error && (
                        <div className="fpm-error-msg">
                            <FaExclamationTriangle />
                            <p>{error}</p>
                            <button className="fpm-btn fpm-btn--primary" onClick={handleDownload}>
                                <FaDownload /> Descargar directamente
                            </button>
                        </div>
                    )}

                    {!loading && !error && renderPreview()}
                </div>
            </div>
        </div>
    );
};

export default FilePreviewModal;
